import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { AED, fmtDateTime } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const ask = (msg) => (window.prompt(msg) || "").trim();

export const hideListing = async (id, done) => {
  const reason = ask("Reason for hiding this listing (shown to the seller):");
  if (!reason) return;
  try { await api.post(`/admin/listings/${id}/hide`, { reason }); toast.success("Listing hidden — seller told"); done && done(); }
  catch { toast.error("Could not hide listing"); }
};

function FlagList({ flags, uid, onChange, openUser }) {
  const review = async (key) => {
    const note = ask("Note for marking this flag Reviewed — OK:");
    try { await api.post(`/admin/users/${uid}/flags/${key}/review`, { note }); toast.success("Flag reviewed"); onChange(); }
    catch { toast.error("Could not save"); }
  };
  if (!flags?.length) return <p className="text-sm text-slate-400">No risk flags.</p>;
  return (
    <div className="space-y-2">
      {flags.map((fl) => (
        <div key={fl.key} className="rounded-lg border border-slate-200 bg-white p-2" data-testid={`risk-flag-${fl.key}`}>
          <p className={`text-sm font-medium ${fl.reviewed ? "text-slate-400 line-through" : "text-slate-800"}`}>{fl.reason}</p>
          <p className="text-xs text-slate-500">{fl.details}</p>
          {fl.reviewed && <p className="text-xs text-emerald-600" data-testid="risk-flag-reviewed">Reviewed — OK{fl.reviewed_by ? ` by ${fl.reviewed_by}` : ""}{fl.reviewed_at ? ` · ${fmtDateTime(fl.reviewed_at)}` : ""}{fl.review_note ? ` · ${fl.review_note}` : ""}</p>}
          <div className="mt-1 flex flex-wrap gap-2 text-xs">
            {(fl.listing_ids || []).map((lid) => (
              <span key={lid} className="flex items-center gap-1">
                <Link to={`/listing/${lid}`} target="_blank" className="text-primary underline" data-testid="risk-flag-listing-link">Listing {lid.slice(0, 6)}</Link>
                <button className="text-red-600 hover:underline" data-testid="risk-flag-hide" onClick={() => hideListing(lid, onChange)}>Hide</button>
              </span>
            ))}
            {(fl.linked || (fl.linked_ids || []).map((id) => ({ id, name: "Linked account" }))).map((a) => <button key={a.id} className="text-primary underline" data-testid="risk-flag-linked" onClick={() => openUser(a.id)}>{a.name}</button>)}
          </div>
          {!fl.reviewed && <Button size="sm" variant="outline" className="mt-1 h-7 rounded-full text-xs" data-testid={`risk-flag-review-${fl.key}`} onClick={() => review(fl.key)}>Reviewed — OK</Button>}
        </div>
      ))}
    </div>
  );
}

export function RiskUserDialog({ row, onClose, onChange, openUser }) {
  return (
    <Dialog open={!!row} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85vh] max-w-xl overflow-y-auto rounded-2xl" data-testid="risk-user-dialog">
        <DialogHeader><DialogTitle>Risk flags — {row?.name}</DialogTitle><DialogDescription>{row?.email}</DialogDescription></DialogHeader>
        {row && <FlagList flags={row.flags} uid={row.id} onChange={onChange} openUser={openUser} />}
        <Button variant="outline" className="rounded-full" data-testid="risk-open-user" onClick={() => openUser(row.id)}>Open user</Button>
      </DialogContent>
    </Dialog>
  );
}

export function DupReviewDialog({ group, onClose, onDone, openUser }) {
  const [d, setD] = useState(null);
  const [note, setNote] = useState("");
  const load = () => group && api.get("/admin/duplicates/review", { params: { seller_id: group.seller_id, norm: group.normalised_title } }).then(({ data }) => setD(data));
  useEffect(() => { setD(null); setNote(""); load(); /* eslint-disable-next-line */ }, [group]);
  const post = async (path, body, msg) => { try { await api.post(path, body); toast.success(msg); onDone(); } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); } };
  const body = group ? { seller_id: group.seller_id, norm: group.normalised_title } : {};
  return (
    <Dialog open={!!group} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto rounded-2xl" data-testid="dup-review-dialog">
        <DialogHeader><DialogTitle>Possible duplicate — review</DialogTitle><DialogDescription>Same seller, same title. Compare the listings and decide.</DialogDescription></DialogHeader>
        {!d ? <p className="text-sm text-slate-400">Loading…</p> : (<>
          <div className="grid gap-4 sm:grid-cols-2">
            {d.listings.map((l) => (
              <div key={l.id} className="rounded-xl border border-slate-200 p-3" data-testid="dup-review-card">
                <img src={l.ai_thumbnail || l.seller_photos?.[0]} alt="" className="aspect-square w-full rounded-lg bg-slate-100 object-cover" />
                <Link to={`/listing/${l.id}`} target="_blank" className="mt-2 block font-semibold text-slate-900 hover:underline">{l.title}</Link>
                <p className="text-sm font-bold text-primary" data-testid="dup-review-price">{l.listing_type === "free" ? "Free" : AED(l.listing_price)}</p>
                <p className="text-xs text-slate-500">{l.condition} · {l.status}</p>
                <div className="mt-2 grid grid-cols-4 gap-1" data-testid="dup-review-seller-photos">
                  {(l.seller_photos || []).map((p, i) => <img key={i} src={p} alt="" className="aspect-square w-full rounded bg-slate-100 object-cover" />)}
                </div>
                <Button size="sm" variant="outline" className="mt-2 rounded-full text-red-600" data-testid="dup-review-hide" onClick={() => hideListing(l.id, onDone)}>Hide this listing</Button>
              </div>
            ))}
          </div>
          <div className="rounded-xl bg-slate-50 p-3 text-sm" data-testid="dup-review-seller">
            <p><b>{d.seller.name}</b> · {d.seller.email} · {d.seller.mobile} · ID {d.seller.user_id}</p>
            <button className="text-xs text-primary underline" data-testid="dup-review-open-user" onClick={() => openUser(d.seller.id)}>Open user</button>
            {(() => { const others = (d.flags || []).filter((f) => !f.reviewed && f.key !== "possible_duplicate").length; return others > 0 ? <p className="mt-1 text-xs" data-testid="dup-review-other-flags">This seller has {others} other Risk flag{others === 1 ? "" : "s"} — <Link to="/admin?tab=risk" onClick={onClose} className="text-primary underline">view in Risk tab</Link></p> : null; })()}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note — why these aren't duplicates (a few words)" className="h-9 max-w-sm rounded-lg" data-testid="dup-review-note" />
            <Button size="sm" className="rounded-full bg-emerald-600" disabled={note.trim().split(/\s+/).filter(Boolean).length < 3} data-testid="dup-review-dismiss" onClick={() => post("/admin/duplicates/dismiss", { ...body, note }, "Marked not a duplicate")}>Not a duplicate</Button>
            <Button size="sm" variant="outline" className="rounded-full" data-testid="dup-review-ask" onClick={() => post("/admin/duplicates/ask-seller", body, "Email sent to seller")}>Ask the seller</Button>
            {d.asked_at && <span className="text-xs text-slate-400" data-testid="dup-review-asked-at">Seller asked {fmtDateTime(d.asked_at)}</span>}
          </div>
        </>)}
      </DialogContent>
    </Dialog>
  );
}
